import type { Id as GuideId, ReportGuideId } from '../../../domain/guides/types.no-deps'
import type { FileId } from '../../../domain/files/types.no-deps'
import type { Answer } from '../../../domain/reports/types.no-deps'
import type { Flow } from '../../../domain/flows/types.no-deps'
import type { ChatId } from '../../../domain/aiChats/types.no-deps'
import type { Embedding } from '../../../domain/embeddings/types.no-deps'

export const MAX_CHAT_FILE_CONTENTS_LENGTH = 1100000 // 1.1m chars takes ~1 min to process.

export type ChunkOptions = {
    version: string

    // Defines the size in tokens of the chunks generated. 1 token ~= 4 characters, 100 tokens ~= 75 words
    chunkSize?: number
    subChunkSize?: number

    // Used for naming a set of chunks, ex. 'chatbot-512', 'guides-search-32'
    chunkSetName?: string
    subChunkSetName?: string

    // Whether content should be translated to English before generating its embedding
    translateContent?: boolean
}

type FileGuide = {
    tag: 'fileGuide'
    fileKey: string
    fileId: FileId
    guideId: GuideId
    description: string | null
} & ChunkOptions

type FlowGuide = {
    tag: 'flowGuide'
    flow: Flow
    workflowId: number
    guideId: GuideId
    description: string | null
} & ChunkOptions

type Report = {
    tag: 'report'
    answers: Answer[]
    reportId: number
    subjectUnitId?: number
    unitNames: Array<[number, string]>
} & ChunkOptions

type ReportGuide = {
    tag: 'reportGuide'
    guideId: GuideId
    reportId: ReportGuideId
    description: string | null
} & ChunkOptions

type UrlGuide = {
    tag: 'urlGuide'
    guideId: GuideId
    url: string
    description: string | null
} & ChunkOptions

type AiChatFile = {
    tag: 'aiChatFile'
    fileKey: string
    fileId: FileId
    chatId: ChatId
} & ChunkOptions

export type IngestableEntity = FileGuide | FlowGuide | Report | ReportGuide | UrlGuide | AiChatFile

export type IngestEntityLambdaEvent = {
    entity: IngestableEntity
    customerSlug: string

    // Override url and auth token to allow us to push generated embeddings to arbitrary servers
    // Useful for testing the 'push' functionality with local server through ngrok
    serverUrlOverride?: string
    authTokenOverride?: string

    // From the Lambda side, this specifies whether to return the embeddings in the same function invocation or to
    // 'push' the embeddings to the customer server using the embeddings HTTP API endpoints.
    mode: 'push' | 'return'
}

export function isGuideIngestableEntity(
    entity: IngestableEntity
): entity is FileGuide | FlowGuide | ReportGuide | UrlGuide {
    if (entity.tag === 'report') {
        return false
    }
    return true
}

export function hasEntityDescription(
    entity: IngestableEntity
): entity is IngestableEntity & { description: string | null } {
    return 'description' in entity
}

export type ContentTooLongError = { code: 'content-too-long'; contentLength: number }
export type InvalidFileError = { code: 'invalid-file' }
export type UnsupportedFileError = { code: 'unsupported-file' }
export type IngestEntityError = ContentTooLongError | InvalidFileError | UnsupportedFileError

export type IngestEntityResultFragment =
    | {
          tag: 'embedding'
          embedding: Embedding
      }
    | { tag: 'error'; error: IngestEntityError }
